import * as React from "react";
import { cn } from "@/lib/utils";

export type RangeSliderProps = {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  /** Disparado ao soltar o thumb / terminar a interação. */
  onCommit?: (value: [number, number]) => void;
  disabled?: boolean;
  className?: string;
  ariaLabelMin?: string;
  ariaLabelMax?: string;
};

const clamp = (value: number, lower: number, upper: number) =>
  Math.min(Math.max(value, lower), upper);

const decimalsOf = (step: number) => {
  const asString = String(step);
  if (asString.includes("e-")) return Number(asString.split("e-")[1]) || 0;
  const dot = asString.indexOf(".");
  return dot === -1 ? 0 : asString.length - dot - 1;
};

/**
 * Range Slider de dois thumbs, sem dependências externas.
 *
 * - respeita min/max/step (inclusive valores decimais);
 * - impede que o thumb inicial ultrapasse o final (e vice-versa);
 * - trata min === max (fica desabilitado, sem divisão por zero);
 * - suporta mouse, toque e teclado.
 */
export const RangeSlider = ({
  min,
  max,
  step = 1,
  value,
  onChange,
  onCommit,
  disabled = false,
  className,
  ariaLabelMin = "Valor inicial",
  ariaLabelMax = "Valor final",
}: RangeSliderProps) => {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const draggingRef = React.useRef<0 | 1 | null>(null);

  const span = max - min;
  const isDegenerate = !Number.isFinite(span) || span <= 0;
  const isDisabled = disabled || isDegenerate;
  const decimals = React.useMemo(() => decimalsOf(step), [step]);

  const [lower, upper] = React.useMemo<[number, number]>(() => {
    const a = clamp(Number.isFinite(value?.[0]) ? value[0] : min, min, max);
    const b = clamp(Number.isFinite(value?.[1]) ? value[1] : max, min, max);
    return a <= b ? [a, b] : [b, a];
  }, [value, min, max]);

  const roundToStep = React.useCallback(
    (raw: number) => {
      if (!Number.isFinite(step) || step <= 0) return clamp(raw, min, max);
      const stepped = Math.round((raw - min) / step) * step + min;
      return Number(clamp(stepped, min, max).toFixed(decimals));
    },
    [min, max, step, decimals]
  );

  const toPercent = React.useCallback(
    (v: number) => (isDegenerate ? 0 : ((clamp(v, min, max) - min) / span) * 100),
    [isDegenerate, min, max, span]
  );

  const valueFromClientX = React.useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return min;
      const rect = track.getBoundingClientRect();
      if (rect.width === 0) return min;
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      return roundToStep(min + ratio * span);
    },
    [min, span, roundToStep]
  );

  const emit = React.useCallback(
    (index: 0 | 1, next: number, commit: boolean) => {
      const nextValue: [number, number] =
        index === 0 ? [Math.min(next, upper), upper] : [lower, Math.max(next, lower)];

      onChange(nextValue);
      if (commit) onCommit?.(nextValue);
    },
    [lower, upper, onChange, onCommit]
  );

  const nearestThumb = React.useCallback(
    (candidate: number): 0 | 1 => {
      const distanceToLower = Math.abs(candidate - lower);
      const distanceToUpper = Math.abs(candidate - upper);
      if (distanceToLower === distanceToUpper) return candidate < lower ? 0 : 1;
      return distanceToLower < distanceToUpper ? 0 : 1;
    },
    [lower, upper]
  );

  const handlePointerDown = (index: 0 | 1) => (event: React.PointerEvent) => {
    if (isDisabled) return;
    event.preventDefault();
    event.stopPropagation();
    draggingRef.current = index;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    const index = draggingRef.current;
    if (isDisabled || index === null) return;
    emit(index, valueFromClientX(event.clientX), false);
  };

  const endDrag = (event: React.PointerEvent) => {
    const index = draggingRef.current;
    if (index === null) return;
    draggingRef.current = null;
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    emit(index, valueFromClientX(event.clientX), true);
  };

  const handleTrackPointerDown = (event: React.PointerEvent) => {
    if (isDisabled) return;
    const candidate = valueFromClientX(event.clientX);
    emit(nearestThumb(candidate), candidate, true);
  };

  const handleKeyDown = (index: 0 | 1) => (event: React.KeyboardEvent) => {
    if (isDisabled) return;
    const current = index === 0 ? lower : upper;
    const bigStep = Math.max(step, span / 10);
    let next: number | null = null;

    switch (event.key) {
      case "ArrowLeft":
      case "ArrowDown":
        next = current - step;
        break;
      case "ArrowRight":
      case "ArrowUp":
        next = current + step;
        break;
      case "PageDown":
        next = current - bigStep;
        break;
      case "PageUp":
        next = current + bigStep;
        break;
      case "Home":
        next = min;
        break;
      case "End":
        next = max;
        break;
      default:
        return;
    }

    event.preventDefault();
    emit(index, roundToStep(next), true);
  };

  const thumbClass = cn(
    "absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary bg-background shadow",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
    isDisabled ? "cursor-not-allowed opacity-60" : "cursor-grab active:cursor-grabbing"
  );

  return (
    <div
      className={cn("relative flex h-5 w-full touch-none select-none items-center", className)}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div
        ref={trackRef}
        className={cn(
          "relative h-1.5 w-full rounded-full bg-muted",
          isDisabled ? "cursor-not-allowed" : "cursor-pointer"
        )}
        onPointerDown={handleTrackPointerDown}
      >
        <div
          className="absolute h-full rounded-full bg-primary"
          style={{
            left: `${toPercent(lower)}%`,
            width: `${Math.max(toPercent(upper) - toPercent(lower), 0)}%`,
          }}
        />
        <span
          role="slider"
          tabIndex={isDisabled ? -1 : 0}
          aria-label={ariaLabelMin}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={lower}
          aria-disabled={isDisabled}
          className={thumbClass}
          style={{ left: `${toPercent(lower)}%` }}
          onPointerDown={handlePointerDown(0)}
          onKeyDown={handleKeyDown(0)}
        />
        <span
          role="slider"
          tabIndex={isDisabled ? -1 : 0}
          aria-label={ariaLabelMax}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={upper}
          aria-disabled={isDisabled}
          className={thumbClass}
          style={{ left: `${toPercent(upper)}%` }}
          onPointerDown={handlePointerDown(1)}
          onKeyDown={handleKeyDown(1)}
        />
      </div>
    </div>
  );
};
